import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { LangSwitch } from "@/components/LangSwitch";

const nav = [
  { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen" },
  { to: "/shopify-theme-anpassung", label: "Anpassungen" },
  { to: "/shopify-conversion-optimierung", label: "Conversion" },
  { to: "/cases", label: "Kundenprojekte" },
  { to: "/shopify-freelancer", label: "Shopify Freelancer" },
  { to: "/faq", label: "FAQ" },
] as const;

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showLangHint, setShowLangHint] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Menü bei Navigation schließen.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);


  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        const max =
          document.documentElement.scrollHeight - window.innerHeight || 1;
        setScrolled(y > 24);
        setProgress(Math.min(1, Math.max(0, y / max)));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Kein automatischer Redirect mehr: Suchmaschinen und KI-Crawler sollen die
  // deutsche Startseite unter "/" sehen. Englischsprachige Besucher bekommen
  // einmalig einen dezenten Hinweis auf die englische Fassung.
  useEffect(() => {
    try {
      if (window.localStorage.getItem("lang-pref")) return;
      if (window.location.pathname !== "/") return;
      const lang = (navigator.language || "").toLowerCase();
      if (lang.startsWith("en")) setShowLangHint(true);
    } catch {
      /* ignore */
    }
  }, []);


  return (
    <header
      className={`sticky top-0 z-30 border-b transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled
          ? "border-border bg-background/70 shadow-[0_10px_30px_-20px_hsl(0_0%_0%/0.9)] backdrop-blur-xl"
          : "border-border/40 bg-background/40 backdrop-blur-sm"
      }`}
    >
      <div
        className={`mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 transition-all sm:gap-4 sm:px-5 duration-300 ${
          scrolled ? "h-14" : "h-20"
        }`}
      >

        <Link
          to="/"
          className={`whitespace-nowrap font-display font-bold tracking-tight transition-all duration-300 ${
            scrolled ? "text-[13px] sm:text-sm" : "text-[13px] sm:text-base"
          }`}
        >
          SHOPIFY<span className="text-primary">-</span>FREELANCER
        </Link>

        <nav className="hidden items-center gap-5 text-sm text-muted-foreground lg:flex">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              activeProps={{ className: "text-foreground font-medium" }}
              className="story-link transition-colors hover:text-foreground"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-3">
          <div className="hidden sm:block">
            <LangSwitch current="de" />
          </div>

          <Link
            to="/kontakt"
            className={`whitespace-nowrap rounded-full bg-primary font-semibold text-primary-foreground transition-all duration-300 hover:opacity-90 ${
              scrolled ? "px-3 py-2 text-xs sm:px-4" : "px-3 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm"
            }`}
          >
            Einschätzung
          </Link>
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-foreground lg:hidden"
          >
            
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

      </div>

      {menuOpen && (
        <nav
          id="mobile-nav"
          className="border-t border-border bg-background/95 backdrop-blur lg:hidden"
        >
          <ul className="mx-auto max-w-6xl px-5 py-2">
            {nav.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  onClick={() => setMenuOpen(false)}
                  activeProps={{ className: "text-primary" }}
                  className="flex min-h-[44px] items-center border-b border-border/60 text-sm font-medium"
                >
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/kontakt"
                onClick={() => setMenuOpen(false)}
                className="flex min-h-[44px] items-center border-b border-border/60 text-sm font-medium"
              >
                Kontakt
              </Link>
            </li>
            <li>
              <Link
                to="/partner-werden"
                onClick={() => setMenuOpen(false)}
                className="flex min-h-[44px] items-center border-b border-border/60 text-sm text-muted-foreground"
              >
                Partner werden
              </Link>
            </li>
            <li className="flex min-h-[44px] items-center sm:hidden">
              <LangSwitch current="de" />
            </li>
          </ul>
        </nav>
      )}


      {showLangHint && (
        <div className="border-t border-border/60 bg-card/80 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-2 text-xs text-muted-foreground">
            <span>This site is also available in English.</span>
            <div className="flex shrink-0 items-center gap-3">
              <a
                href="/en"
                onClick={() => {
                  try {
                    window.localStorage.setItem("lang-pref", "en");
                  } catch {
                    /* ignore */
                  }
                }}
                className="font-semibold text-primary"
              >
                Switch to English
              </a>
              <button
                type="button"
                onClick={() => {
                  try {
                    window.localStorage.setItem("lang-pref", "de");
                  } catch {
                    /* ignore */
                  }
                  setShowLangHint(false);
                }}
                className="text-muted-foreground underline-offset-2 hover:underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        aria-hidden
        className="h-px w-full origin-left bg-gradient-to-r from-primary via-accent to-primary transition-transform duration-150"
        style={{ transform: `scaleX(${progress})` }}
      />

    </header>

  );
}
